/**
 * Admin → Settings → Company Research → Dictionaries card (E07-F03-T03, web side).
 *
 * 1:1 port of the legacy prototype `research.proto.js` renderSettings Dictionaries
 * block + `dictCol` / `addDictTerm` / `removeChip`
 * (../EER app/prototype/assets/research.proto.js L1319-1357; MOCK.dictionaries in
 * research.data.js). One card titled "Dictionaries" with the caption verbatim, and
 * a 3-column grid (Industries / Sub-categories / Locations):
 *
 *   - each column shows its active terms as chips (`bg-slate-100 …` + a `×` remove
 *     button) inside a `max-h-32 overflow-auto` scroll area (T03 acceptance #4);
 *   - a count badge in the header shows `items.length` (T03 acceptance #2);
 *   - an "Add term, Enter" input at the bottom — Enter commits a trimmed,
 *     non-duplicate term (T03 acceptance #3).
 *
 * Every add/remove POSTs `/research/dictionaries` (research_save_dictionary) and
 * the server returns the fresh active snapshot, which re-renders the chips AND
 * (because the Intake form subscribes to the same query) the Intake datalists.
 *
 * Transport: researchDictionariesApi.ts → the shared `apiClient`. Pure rules:
 * researchDictionariesModel.ts.
 */
import { useState, type KeyboardEvent, type JSX } from 'react';
import { DICT_COLUMNS, type DictKey } from '@eer/shared';
import { useToast } from '../ui/index.js';
import {
  buildAddInput,
  buildRemoveInput,
  isAddTermKey,
  termCount,
  termToAdd,
  valuesForKey,
} from './researchDictionariesModel.js';
import { errorMessage } from '../api/client.js';
import {
  useResearchDictionaries,
  useSaveDictionary,
  useUnmatchedSubcategories,
} from './researchDictionariesApi.js';

/** Slug for a column's testids (e.g. `industry` → `dict-col-industry`). */
function colTestId(key: DictKey): string {
  return `dict-col-${key}`;
}

/** One dictionary column (prototype `dictCol`). */
function DictionaryColumn(props: {
  dictKey: DictKey;
  title: string;
  values: readonly string[];
  busy: boolean;
  onAdd: (key: DictKey, value: string) => void;
  onRemove: (key: DictKey, value: string) => void;
}): JSX.Element {
  const { dictKey, title, values, busy, onAdd, onRemove } = props;
  const [draft, setDraft] = useState('');

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (!isAddTermKey(e)) return;
    e.preventDefault();
    const value = termToAdd(draft, values);
    setDraft('');
    if (value === null) return;
    onAdd(dictKey, value);
  }

  return (
    <div className="border border-gray-200 rounded-lg p-3" data-testid={colTestId(dictKey)}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{title}</span>
        <span
          className="text-[10px] bg-slate-100 text-slate-400 px-2 py-0.5 rounded-full"
          data-testid={`${colTestId(dictKey)}-count`}
        >
          {termCount(values)}
        </span>
      </div>

      <div
        className="flex flex-wrap gap-1.5 mb-2 max-h-32 overflow-auto"
        data-testid={`${colTestId(dictKey)}-chips`}
      >
        {values.map((value) => (
          <span
            key={value}
            className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
            data-testid="dict-chip"
            data-dict-value={value}
          >
            {value}
            <button
              type="button"
              className="text-slate-400 hover:text-red-500 font-bold leading-none"
              disabled={busy}
              aria-label={`Remove ${value}`}
              title="Remove"
              onClick={() => onRemove(dictKey, value)}
            >
              {'×'}
            </button>
          </span>
        ))}
      </div>

      <input
        type="text"
        className="w-full border rounded p-1.5 text-xs focus:border-[#D4AF37] outline-none"
        placeholder="Add term, Enter"
        aria-label={`Add term to ${title}`}
        data-testid={`${colTestId(dictKey)}-input`}
        value={draft}
        disabled={busy}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
}

/**
 * E07-F02-T14 — request sub-categories no industry-map rule recognizes (they go to
 * ZoomInfo as a raw phrase). Each row has a rule input prefilled with
 * `<phrase> => `; Enter adds it to the industry map (validated server-side), and
 * the list refreshes (an added rule removes the phrase).
 */
function UnmatchedSubcategoriesPanel(props: {
  busy: boolean;
  onAddRule: (rule: string) => void;
}): JSX.Element | null {
  const { busy, onAddRule } = props;
  const unmatched = useUnmatchedSubcategories();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const items = unmatched.data ?? [];
  if (unmatched.isLoading || unmatched.isError) return null;

  return (
    <div
      className="border border-amber-200 bg-amber-50 rounded-lg p-3 mt-4"
      data-testid="dict-unmatched"
    >
      <div className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-1">
        Unrecognized sub-categories ({items.length})
      </div>
      <p className="text-xs text-amber-800 mb-2">
        These request sub-categories match no ZoomInfo industry-map rule, so discovery searches them
        as a raw phrase (often 0 companies). Complete the rule and press Enter to add it.
      </p>
      {items.length === 0 ? (
        <div className="text-xs text-gray-500" data-testid="dict-unmatched-empty">
          Every sub-category in the requests has a rule.
        </div>
      ) : (
        <ul className="space-y-1.5 max-h-64 overflow-auto">
          {items.map((item) => {
            const draft = drafts[item.phrase] ?? item.ruleTemplate;
            return (
              <li key={item.phrase} data-testid="dict-unmatched-item" data-phrase={item.phrase}>
                <div className="text-xs text-slate-700">
                  <b>{item.phrase}</b>{' '}
                  <span className="text-slate-400">
                    · {item.requestCount} {item.requestCount === 1 ? 'request' : 'requests'} (#
                    {item.requestIds.join(', #')})
                  </span>
                </div>
                <input
                  type="text"
                  className="w-full border rounded p-1.5 text-xs bg-white focus:border-[#D4AF37] outline-none"
                  aria-label={`Add industry-map rule for ${item.phrase}`}
                  data-testid="dict-unmatched-rule-input"
                  value={draft}
                  disabled={busy}
                  onChange={(e) => setDrafts((d) => ({ ...d, [item.phrase]: e.target.value }))}
                  onKeyDown={(e) => {
                    if (!isAddTermKey(e)) return;
                    e.preventDefault();
                    const rule = draft.trim();
                    if (rule === '' || rule === item.ruleTemplate.trim()) return;
                    onAddRule(rule);
                  }}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Dictionaries card — 3 columns of autocomplete source terms. */
export function DictionariesCard(): JSX.Element {
  const toast = useToast();
  const dictionariesQuery = useResearchDictionaries();
  const save = useSaveDictionary();
  const busy = save.isPending;

  function addTerm(key: DictKey, value: string): void {
    save.mutate(buildAddInput(key, value), {
      onSuccess: () => toast.success('Term added to dictionary'),
      onError: (e) => toast.error(e.detail.message),
    });
  }

  function removeTerm(key: DictKey, value: string): void {
    save.mutate(buildRemoveInput(key, value), {
      onSuccess: () => toast.success('Term removed'),
      onError: (e) => toast.error(e.detail.message),
    });
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mt-6" data-testid="dictionaries-card">
      <h2 className="text-xl font-bold mb-1">Dictionaries</h2>
      <p className="text-xs text-gray-400 mb-4">
        Autocomplete sources for the New-Request form (Industry · Sub-category · Locations). Add
        terms here; they appear as suggestions during intake.
      </p>

      {dictionariesQuery.isLoading ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : dictionariesQuery.isError || !dictionariesQuery.data ? (
        <div className="text-sm text-red-600" data-testid="dictionaries-error">
          {errorMessage(dictionariesQuery.error, 'Failed to load dictionaries')}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {DICT_COLUMNS.map((col) => (
            <DictionaryColumn
              key={col.key}
              dictKey={col.key}
              title={col.title}
              values={valuesForKey(dictionariesQuery.data, col.key)}
              busy={busy}
              onAdd={addTerm}
              onRemove={removeTerm}
            />
          ))}
        </div>
      )}
      <UnmatchedSubcategoriesPanel
        busy={busy}
        onAddRule={(rule) => addTerm('industry_map', rule)}
      />
    </div>
  );
}

export default DictionariesCard;
